import { inject, Injectable } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";

export interface PlaceDetails {
    coordinates: google.maps.LatLngLiteral;
    city: string;
    country?: string | null;
    countryCode?: string | null;
    postalCode?: string | null;
    street?: string | null;
}

@Injectable({ providedIn: 'root' })
export class GoogleMapsService {
    private readonly transloco = inject(TranslocoService);
    private loadingPromise?: Promise<void>;

    load(apiKey: string): Promise<void> {
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        this.loadingPromise = new Promise((resolve, reject) => {
            // Already loaded
            if ((window as any).google?.maps) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=geometry,places`;
            script.async = true;
            script.defer = true;

            script.onload = () => resolve();
            script.onerror = () => reject('Google Maps failed to load');

            document.head.appendChild(script);
        });

        return this.loadingPromise;
    }

    async geocodeAddress(address: string): Promise<google.maps.LatLngLiteral> {
        const geocoder = new google.maps.Geocoder();

        const response = await geocoder.geocode({ address });

        const first = response.results?.[0];

        if (!first) {
        throw new Error('ZERO_RESULTS');
        }

        const loc = first.geometry.location;
        return { lat: loc.lat(), lng: loc.lng() };
    }

    async getPlacePredictionsByGeocode(
        input: string,
        restrictions?: { countries: string[] }
    ): Promise<google.maps.places.AutocompletePrediction[]> {
        if (!input) return [];

        const service = new google.maps.places.AutocompleteService();
        const currentLanguage = this.transloco.getActiveLang();
        const response = await service.getPlacePredictions({
            input,
            ...(restrictions?.countries?.length && { componentRestrictions: { country: restrictions.countries } }),
            types: ['geocode'],
            language: currentLanguage,
        });

        // Filter out the "only-country" option 
        return response.predictions.filter(p => !p.types.includes('country'));
    }

    async geocodePlaceDetails(placeId: string): Promise<PlaceDetails | null> {
        const geocoder = new google.maps.Geocoder();
        const currentLanguage = this.transloco.getActiveLang();
        const response = await geocoder.geocode({ placeId, language: currentLanguage});

        return this.toPlaceDetails(response.results?.[0]);
    }

    async geocodePlaceDetailsByCoordinates(coords: google.maps.LatLngLiteral): Promise<PlaceDetails | null> {
        const geocoder = new google.maps.Geocoder();
        const currentLanguage = this.transloco.getActiveLang();
        const response = await geocoder.geocode({ location: coords, language: currentLanguage });

        return this.toPlaceDetails(response.results?.[0]);
    }

    private toPlaceDetails(result: google.maps.GeocoderResult | undefined): PlaceDetails | null {
        if (!result) return null;

        const loc = result.geometry.location;
        const addressComponents = result.address_components ?? [];

        const country = this.findAddressComponent(addressComponents, 'country')?.long_name ?? null;
        const countryCode = this.findAddressComponent(addressComponents, 'country')?.short_name ?? null;
        const route = this.findAddressComponent(addressComponents, 'route')?.long_name ?? null;
        const streetNumber = this.findAddressComponent(addressComponents, 'street_number')?.long_name ?? null;
        const city = this.findAddressComponent(addressComponents, 'locality')?.long_name ?? null;
        const postalCode = this.findAddressComponent(addressComponents, 'postal_code')?.long_name ?? null;

        return {
            coordinates: { lat: loc.lat(), lng: loc.lng() },
            city,
            country,
            countryCode,
            postalCode,
            street: route ? (streetNumber ? `${route} ${streetNumber}` : route) : null,
        };
    }

    private findAddressComponent(
        components: google.maps.GeocoderAddressComponent[],
        type: string,
    ): google.maps.GeocoderAddressComponent | undefined {
        return components.find((c) => c.types.includes(type));
    }
}